import { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import axios from "axios";


const BATCH = 100;
const DEBUG_DETECTION = true;
const API_URL = "https://service.galosolar.com/api/service/showImage";
const API_PARAM = "saralId"; // spelled as specified
const IMAGE_BASE_URL = "https://service.galosolar.com/api/"; // each path in response.data is appended to this


const imageUrl = (path) => new URL(path, IMAGE_BASE_URL).href;
const fileLabel = (path) => path.split("/").pop().split("-")[0]; // "panelPhoto-1790...jpg" -> "panelPhoto"


const normKey = (s) => String(s).toLowerCase().replace(/[^a-z0-9]/g, "");
const FIELD_ALIASES = {
    latitude: ["latitude", "lat"],
    longitude: ["longitude", "long", "lng", "lon"],
    farmerName: ["farmername", "farmer"],
    district: ["district", "districtname"],
    village: ["village"],
    category: ["categoryofpump", "pumpcategory"],
    capacity: ["pumpcapacity", "capacity"],
    subType: ["pumpsubtype", "subtype"],
};

const FIELD_NAMES = {
    latitude: "Latitude", longitude: "Longitude", farmerName: "Farmer Name", district: "District",
    village: "village", category: "Category of Pump", capacity: "Pump Capacity", subType: "Pump Sub Type", 
};

const hasField = (row, f) => Object.keys(row).some((k) => FIELD_ALIASES[f].includes(normKey(k)));
function pick(row, f) {
    if (!row) return "";
    const k = Object.keys(row).find((key) => FIELD_ALIASES[f].includes(normKey(key)));
    return k === undefined ? "" : String(row[k]).trim();
}

// Lines printed on the image, one per row, in this order.
function buildLines(row, id) {
    const pump = ["category", "capacity", "subType"].map((f) => pick(row, f)).filter(Boolean).join(" ");
    const districtAndVillage = [ "district", "village" ].map((f) => pick(row, f)).filter(Boolean).join(", ")
    return [
        ["Latitude", pick(row, "latitude") || "-"],
        ["Longitude", pick(row, "longitude") || "-"],
        ["Saral Id", id],
        ["Farmer Name", pick(row, "farmerName") || "-"],
        ["District", districtAndVillage || "-"],
        ["Pump Capacity", pump || "-"],
    ];
}

const missingColumns = (row) => (row ? Object.keys(FIELD_ALIASES).filter((f) => !hasField(row, f)).map((f) => FIELD_NAMES[f]) : []);

/* ---------- limit parallel API requests to 6 ---------- */
let active = 0;
const queue = [];
const limit = (fn) =>
    new Promise((resolve, reject) => {
        const run = async () => {
            active++;
            try { resolve(await fn()); } catch (e) { reject(e); }
            finally { active--; queue.length && queue.shift()(); }
        };
        active < 6 ? run() : queue.push(run);
    });

async function fetchImagePaths(id) {
    const { data } = await axios.get(API_URL, { params: { [API_PARAM]: id } });
    return Array.isArray(data?.data) ? data.data.filter(Boolean) : [];
}


const newStore = () => ({ pending: new Map(), paths: new Map(), loaded: new Set() }); // loaded = image paths whose <img> finished loading
function getPaths(store, id) {
    if (!store.pending.has(id)) {
        store.pending.set(
            id,
            limit(() => fetchImagePaths(id))
                .catch(() => [])
                .then((list) => { store.paths.set(id, list); return list; })
        );
    }
    return store.pending.get(id);
}

/* ---------- AI label placement ---------- */
const loadImg = (blob) =>
    new Promise((res, rej) => {
        const im = new Image();
        im.onload = () => res(im);
        im.onerror = rej;
        im.src = URL.createObjectURL(blob);
    });

// Solar panels aren't a COCO class, so they're found by colour: dark, blue-dominant cells.
function panelGrid(img, cols = 80) {
    const rows = Math.max(1, Math.round((cols * img.naturalHeight) / img.naturalWidth));
    const n = cols * rows;
    const c = document.createElement("canvas");
    c.width = cols; c.height = rows;
    const x = c.getContext("2d", { willReadFrequently: true });
    x.drawImage(img, 0, 0, cols, rows);
    const d = x.getImageData(0, 0, cols, rows).data;

    // 1. raw mask: dark, clearly blue cells (sky, shadows and soil are mostly not)
    const raw = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
        const r = d[i * 4], g = d[i * 4 + 1], b = d[i * 4 + 2];
        const max = Math.max(r, g, b), min = Math.min(r, g, b), lum = (r + g + b) / 3;
        raw[i] = b >= max - 1 && b - r > 20 && max > 0 && (max - min) / max > 0.2 && lum > 20 && lum < 140 ? 1 : 0;
    }
    // 2. remove speckles: keep a cell only if most of its 3x3 neighbourhood is also flagged
    const dense = new Uint8Array(n);
    for (let cy = 0; cy < rows; cy++)
        for (let cx = 0; cx < cols; cx++) {
            let k = 0;
            for (let dy = -1; dy <= 1; dy++)
                for (let dx = -1; dx <= 1; dx++) {
                    const yy = cy + dy, xx = cx + dx;
                    if (yy >= 0 && yy < rows && xx >= 0 && xx < cols) k += raw[yy * cols + xx];
                }
            dense[cy * cols + cx] = k >= 5 ? 1 : 0;
        }
    // 3. keep only large connected blobs: a real panel array is big, stray dark-blue patches are not
    const seen = new Uint8Array(n), out = new Uint8Array(n);
    const minArea = Math.max(8, Math.round(n * 0.015));
    for (let i = 0; i < n; i++) {
        if (!dense[i] || seen[i]) continue;
        const stack = [i], comp = [];
        seen[i] = 1;
        while (stack.length) {
            const j = stack.pop();
            comp.push(j);
            const cx = j % cols, cy = (j - cx) / cols;
            for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
                const xx = cx + dx, yy = cy + dy, k = yy * cols + xx;
                if (xx >= 0 && xx < cols && yy >= 0 && yy < rows && dense[k] && !seen[k]) { seen[k] = 1; stack.push(k); }
            }
        }
        if (comp.length >= minArea) comp.forEach((j) => (out[j] = 1));
    }
    return { g: out, cols, rows };
}

// Collides with a person box, or when more than 3% of the cells under the box are panel.
function hits([x, y, w, h], persons, pg, W, H) {
    for (const [px, py, pw, ph] of persons)
        if (x < px + pw && x + w > px && y < py + ph && y + h > py) return true;
    const x0 = Math.floor((x / W) * pg.cols), x1 = Math.ceil(((x + w) / W) * pg.cols);
    const y0 = Math.floor((y / H) * pg.rows), y1 = Math.ceil(((y + h) / H) * pg.rows);
    let flagged = 0, cells = 0;
    for (let cy = Math.max(0, y0); cy < Math.min(y1, pg.rows); cy++)
        for (let cx = Math.max(0, x0); cx < Math.min(x1, pg.cols); cx++) {
            cells++;
            flagged += pg.g[cy * pg.cols + cx];
        }
    return cells > 0 && flagged / cells > 0.03;
}

async function labelImage(model, blob, lines) {
    const img = await loadImg(blob);
    const W = img.naturalWidth, H = img.naturalHeight;
    const cv = document.createElement("canvas");
    cv.width = W; cv.height = H;
    const ctx = cv.getContext("2d");
    ctx.drawImage(img, 0, 0);

    const pad = Math.round(Math.min(W, H) * 0.01);
    const persons = (await model.detect(cv))
        .filter((p) => p.class === "person" && p.score > 0.45)
        .map((p) => [p.bbox[0] - pad, p.bbox[1] - pad, p.bbox[2] + 2 * pad, p.bbox[3] + 2 * pad]);
    const pg = panelGrid(img);
    // For me only
    if (DEBUG_DETECTION) {
        ctx.fillStyle = "rgba(0,120,255,.35)";
        for (let i = 0; i < pg.g.length; i++)
            if (pg.g[i]) ctx.fillRect(((i % pg.cols) / pg.cols) * W, (Math.floor(i / pg.cols) / pg.rows) * H, W / pg.cols + 1, H / pg.rows + 1);
        ctx.strokeStyle = "red"; ctx.lineWidth = 3;
        persons.forEach(([px, py, pw, ph]) => ctx.strokeRect(px, py, pw, ph));
    }
    const m = 0;
    const maxSize = Math.max(10, Math.round(Math.min(W, H) * 0.028)); // small, plain text
    const minSize = 8, lh = 1.3;
    const widest = (s) => { ctx.font = `${s}px sans-serif`; return Math.max(...lines.map((l) => ctx.measureText(l).width)); };
    // Box (white container) around the text block: padding scales with the font size.
    const boxFor = (s) => {
        const pd = Math.round(s * 0.6);
        return { pd, w: widest(s) + 2 * pd, h: (lines.length - 1) * s * lh + s + 2 * pd };
    };

    let size = maxSize;
    for (; size >= minSize; size -= 1) { // largest size where the whole box avoids people and panels
        const bx = boxFor(size);
        if (bx.w + 2 * m < W && bx.h + 2 * m < H && !hits([m, H - m - bx.h, bx.w, bx.h], persons, pg, W, H)) break;
    }
    size = Math.max(size, minSize);
    const bx = boxFor(size);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(m, H - m - bx.h, bx.w, bx.h);
    ctx.font = `${size}px sans-serif`;
    ctx.textBaseline = "bottom";
    ctx.fillStyle = "#000000";
    lines.forEach((l, i) => ctx.fillText(l, m + bx.pd, H - m - bx.pd - (lines.length - 1 - i) * size * lh));
    return new Promise((r) => cv.toBlob(r, "image/jpeg", 0.92));
}

/* ---------- UI pieces ---------- */
const Icon = ({ d, className = "h-4 w-4" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
        <path d={d} />
    </svg>
);
const ICONS = {
    upload: "M12 16V4m0 0-4 4m4-4 4 4M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3",
    check: "M5 12.5l4.5 4.5L19 7.5",
    left: "M15 18l-6-6 6-6",
    right: "M9 6l6 6-6 6",
    download: "M12 4v12m0 0-4-4m4 4 4-4M4 20h16",
    image: "M4 5h16v14H4zM4 16l5-5 4 4 3-3 4 4",
};

const Btn = ({ primary, small, className = "", ...p }) => (
    <button
        {...p}
        className={`inline-flex items-center justify-center gap-2 rounded-lg border font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black disabled:cursor-not-allowed disabled:opacity-40 ${small ? "px-3 py-1 text-xs" : "px-4 py-2 text-sm"
            } ${primary
                ? "border-yellow-600 bg-yellow-600 text-black enabled:hover:bg-yellow-500"
                : "border-black/20 bg-white text-black enabled:hover:border-yellow-600 enabled:hover:text-yellow-700"
            } ${className}`}
    />
);

const Step = ({ n, title, hint, children, right }) => (
    <section className="rounded-2xl border border-black/10 bg-black/[0.02] p-5">
        <div className="mb-4 flex flex-wrap items-center gap-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-yellow-600 text-sm font-bold text-black">{n}</span>
            <div className="mr-auto">
                <h2 className="text-base font-semibold text-black">{title}</h2>
                {hint && <p className="text-sm text-black/60">{hint}</p>}
            </div>
            {right}
        </div>
        {children}
    </section>
);

function ImageTile({ path, checked, loaded, onLoaded, onToggle }) {
    const [failed, setFailed] = useState(false);
    // Cached images can finish before onLoad is attached, so also check on mount.
    const ref = (el) => { if (el && !loaded && el.complete && el.naturalWidth > 0) onLoaded(path); };
    const selectable = loaded && !failed;
    return (
        <label
            className={`group relative block overflow-hidden rounded-xl bg-white ring-2 transition ${checked ? "ring-yellow-600" : "ring-black/10"
                } ${selectable ? "cursor-pointer hover:ring-yellow-600/60" : "cursor-default"}`}
        >
            <div className="relative aspect-[4/3] bg-black/5">
                {failed ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-xs text-black/60">
                        <Icon d={ICONS.image} className="h-6 w-6 text-yellow-700" />
                        Image failed to load
                    </div>
                ) : (
                    <>
                        {!loaded && <div className="absolute inset-0 animate-pulse bg-black/10" />}
                        <img
                            ref={ref}
                            src={imageUrl(path)}
                            alt={fileLabel(path)}
                            loading="lazy"
                            onLoad={() => onLoaded(path)}
                            onError={() => setFailed(true)}
                            className={`h-full w-full object-cover transition-opacity ${loaded ? "opacity-100" : "opacity-0"}`}
                        />
                        {loaded && (
                            <>
                                <input type="checkbox" checked={checked} onChange={onToggle} className="peer sr-only" aria-label={`Select ${fileLabel(path)}`} />
                                <span
                                    className={`absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-md border-2 transition peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-yellow-600 ${checked ? "border-yellow-600 bg-yellow-600 text-black" : "border-white bg-black/60 text-transparent"
                                        }`}
                                >
                                    <Icon d={ICONS.check} className="h-4 w-4" />
                                </span>
                                {checked && <div className="pointer-events-none absolute inset-0 bg-yellow-600/10" />}
                            </>
                        )}
                    </>
                )}
            </div>
            <div className="border-t border-black/10 px-3 py-1.5">
                <span className="block truncate text-xs text-black/70" title={path}>{fileLabel(path)}</span>
            </div>
        </label>
    );
}

// Heading = Saral Id, with its images below it.
function SaralSection({ id, store, selectedSet, onToggle, onSelectAll, onClear }) {
    const [paths, setPaths] = useState(null);
    const [, bump] = useState(0); // re-render when an image finishes loading

    useEffect(() => {
        let dead = false;
        getPaths(store, id).then((list) => !dead && setPaths(list));
        return () => { dead = true; };
    }, [id, store]);

    const count = selectedSet ? selectedSet.size : 0;
    const loadedPaths = (paths || []).filter((p) => store.loaded.has(p));
    const markLoaded = (path) => { if (!store.loaded.has(path)) { store.loaded.add(path); bump((n) => n + 1); } };
    return (
        <div className="rounded-xl border border-black/10 bg-white p-4">
            <div className="mb-3 flex flex-wrap items-center gap-2">
                <h3 className="mr-auto break-all text-base font-semibold text-yellow-700">{id}</h3>
                {paths && paths.length > 0 && (
                    <>
                        <span className="text-xs text-black/60">{count} of {paths.length} selected</span>
                        <Btn small onClick={() => onSelectAll(id, loadedPaths)} disabled={!loadedPaths.length || count === loadedPaths.length}>Select all</Btn>
                        <Btn small onClick={() => onClear(id)} disabled={!count}>Clear</Btn>
                    </>
                )}
            </div>
            {paths === null ? (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    {[0, 1, 2, 3].map((i) => <div key={i} className="aspect-[4/3] animate-pulse rounded-xl bg-black/10" />)}
                </div>
            ) : paths.length === 0 ? (
                <p className="py-4 text-sm text-black/60">No images found for this Saral Id.</p>
            ) : (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    {paths.map((p) => (
                        <ImageTile key={p} path={p} checked={!!selectedSet?.has(p)} loaded={store.loaded.has(p)} onLoaded={markLoaded} onToggle={() => onToggle(id, p)} />
                    ))}
                </div>
            )}
        </div>
    );
}

/* ---------- page ---------- */
export default function Dashboard() {
    const [ids, setIds] = useState([]);
    const [fileName, setFileName] = useState("");
    const [page, setPage] = useState(0);
    const [selected, setSelected] = useState(() => new Map()); // saralId -> Set of image paths
    const [missing, setMissing] = useState([]); // expected Excel columns that were not found
    const [message, setMessage] = useState(null); // { type: 'info' | 'error' | 'done', text }
    const [progress, setProgress] = useState(null);
    const [drag, setDrag] = useState(false);
    const store = useRef(null);
    if (store.current === null) store.current = newStore();
    const modelRef = useRef(null);
    const rowsById = useRef(new Map()); // saralId -> Excel row

    const total = Math.ceil(ids.length / BATCH);
    const batch = useMemo(() => ids.slice(page * BATCH, (page + 1) * BATCH), [ids, page]);
    const countOf = (list) => list.reduce((n, id) => n + (selected.get(id)?.size || 0), 0);
    const totalSelected = countOf([...selected.keys()]);
    const batchSelected = countOf(batch);
    const busy = progress !== null;

    const readFile = async (f) => {
        if (!f) return;
        try {
            const wb = XLSX.read(await f.arrayBuffer());
            const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
            const norm = (s) => String(s).toLowerCase().replace(/[^a-z]/g, "");
            const key = rows.length && Object.keys(rows[0]).find((k) => norm(k) === "saralid");
            if (!key) return setMessage({ type: "error", text: 'No "Saral Id" column found in the first sheet.' });
            const byId = new Map();
            rows.forEach((r) => { const id = String(r[key]).trim(); if (id && !byId.has(id)) byId.set(id, r); });
            const list = [...byId.keys()];
            rowsById.current = byId;
            setMissing(missingColumns(rows[0]));
            store.current = newStore();
            setSelected(new Map()); setPage(0); setIds(list); setFileName(f.name);
            setMessage(null);
        } catch {
            setMessage({ type: "error", text: "Could not read this file. Upload a valid .xlsx, .xls or .csv." });
        }
    };

    const update = (fn) => setSelected((prev) => { const next = new Map(prev); fn(next); return next; });
    const toggle = (id, path) =>
        update((next) => {
            const set = new Set(next.get(id) || []);
            set.has(path) ? set.delete(path) : set.add(path);
            set.size ? next.set(id, set) : next.delete(id);
        });
    const selectAllFor = (id, paths) => update((next) => next.set(id, new Set(paths)));
    const clearFor = (id) => update((next) => next.delete(id));
    const selectBatch = () =>
        update((next) => batch.forEach((id) => {
            const p = (store.current.paths.get(id) || []).filter((x) => store.current.loaded.has(x)); // loaded images only
            if (p.length) next.set(id, new Set(p));
        }));
    const clearBatch = () => update((next) => batch.forEach((id) => next.delete(id)));

    const run = async () => {
        try {
            setProgress(0);
            setMessage({ type: "info", text: "Loading AI model" });
            if (!modelRef.current) {
                await import("@tensorflow/tfjs");
                modelRef.current = await (await import("@tensorflow-models/coco-ssd")).load();
            }
            const zip = new JSZip();
            const jobs = [...selected].flatMap(([id, set]) => [...set].map((path) => ({ id, path })));
            let done = 0, skipped = 0;
            const used = {};
            for (const { id, path } of jobs) {
                setMessage({ type: "info", text: `Labelling ${done + skipped + 1} of ${jobs.length} (${id})` });
                try {
                    // Reading pixels needs the image server to allow CORS for this site.
                    const blob = (await axios.get(imageUrl(path), { responseType: "blob" })).data;
                    const lines = buildLines(rowsById.current.get(id), id).map(([k, v]) => `${k}: ${v}`);
                    const out = await labelImage(modelRef.current, blob, lines);
                    const safe = id.replace(/[\\/:*?"<>|]/g, "_");
                    used[id] = (used[id] || 0) + 1;
                    zip.file(used[id] === 1 ? `${safe}.jpg` : `${safe}_${used[id]}.jpg`, out);
                    done++;
                } catch { skipped++; }
                setProgress(((done + skipped) / jobs.length) * 100);
            }
            if (!done) throw new Error("No images could be read. The image server may need to allow CORS.");
            setMessage({ type: "info", text: "Building ZIP" });
            const out = await zip.generateAsync({ type: "blob" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(out);
            a.download = "labelled-images.zip";
            a.click();
            setMessage({ type: "done", text: `${done} labelled images downloaded${skipped ? `, ${skipped} skipped (could not be read)` : ""}.` });
        } catch (e) {
            setMessage({ type: "error", text: "Something went wrong: " + e.message });
        }
        setProgress(null);
    };

    const previewId = [...selected.keys()][0] || ids[0] || "SARAL123";
    const previewLines = buildLines(rowsById.current.get(previewId), previewId);

    return (
        <div className="space-y-5 border border-black/10 bg-white p-5 text-black sm:p-6">
            <div>
                <h1 className="text-2xl font-bold text-black">Update Site Images</h1>
                <p className="mt-1 max-w-prose text-sm text-black/70">
                    Upload your Saral IDs, pick the images to keep, and download them with the Saral Id details printed in the bottom-left corner.
                </p>
            </div>

            {/* 1. Upload */}
            <Step n={1} title="Upload Excel" hint='The first sheet needs a column named "Saral Id".'>
                <label
                    onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                    onDragLeave={() => setDrag(false)}
                    onDrop={(e) => { e.preventDefault(); setDrag(false); readFile(e.dataTransfer.files[0]); }}
                    className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center transition ${drag ? "border-yellow-600 bg-yellow-600/10" : "border-black/20 hover:border-yellow-600"
                        }`}
                >
                    <Icon d={ICONS.upload} className="h-7 w-7 text-yellow-600" />
                    <span className="text-sm font-medium text-black">Drop a file here or click to browse</span>
                    <span className="text-xs text-black/60">.xlsx, .xls or .csv</span>
                    <input type="file" accept=".xlsx,.xls,.csv" className="sr-only" onChange={(e) => readFile(e.target.files[0])} />
                </label>
                {ids.length > 0 && (
                    <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-black/70">
                        <span className="font-medium text-black">{fileName}</span>
                        <span><b className="text-yellow-700">{ids.length}</b> unique Saral IDs</span>
                        <span><b className="text-yellow-700">{total}</b> batches of {BATCH}</span>
                    </div>
                )}
            </Step>

            {/* 2. Select */}
            <Step
                n={2}
                title="Select images"
                hint="An image can be selected once it has loaded. Selections stay when you change batch."
                right={
                    ids.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2">
                            <Btn onClick={() => setPage((p) => p - 1)} disabled={page === 0} aria-label="Previous batch">
                                <Icon d={ICONS.left} />
                            </Btn>
                            <select
                                value={page}
                                onChange={(e) => setPage(Number(e.target.value))}
                                className="rounded-lg border border-black/20 bg-white px-3 py-2 text-sm text-black focus:border-yellow-600 focus:outline-none"
                                aria-label="Batch"
                            >
                                {Array.from({ length: total }, (_, i) => (
                                    <option key={i} value={i}>Batch {i + 1} of {total}</option>
                                ))}
                            </select>
                            <Btn onClick={() => setPage((p) => p + 1)} disabled={page >= total - 1} aria-label="Next batch">
                                <Icon d={ICONS.right} />
                            </Btn>
                        </div>
                    )
                }
            >
                {ids.length === 0 ? (
                    <div className="rounded-xl border border-black/10 py-10 text-center text-sm text-black/60">
                        Upload a file in step 1 and your images will appear here.
                    </div>
                ) : (
                    <>
                        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
                            <Btn onClick={selectBatch}>Select all in batch</Btn>
                            <Btn onClick={clearBatch} disabled={!batchSelected}>Clear batch</Btn>
                            <span className="ml-auto text-black/70">
                                <b className="text-yellow-700">{batchSelected}</b> images selected in this batch
                            </span>
                        </div>
                        <div className="space-y-4">
                            {batch.map((id) => (
                                <SaralSection
                                    key={`${page}-${id}`}
                                    id={id}
                                    store={store.current}
                                    selectedSet={selected.get(id)}
                                    onToggle={toggle}
                                    onSelectAll={selectAllFor}
                                    onClear={clearFor}
                                />
                            ))}
                        </div>
                    </>
                )}
            </Step>

            {/* 3. Label content */}
            <Step n={3} title="Label content" hint="Printed in the bottom-left of each image. The size is set by AI so it avoids people and solar panels.">
                {missing.length > 0 && (
                    <p className="mb-3 rounded-lg border border-yellow-600/60 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                        These columns were not found in your Excel file and will print as "-": {missing.join(", ")}.
                    </p>
                )}
                <div className="inline-block rounded-lg bg-black/5 px-4 py-3 text-sm leading-6 text-black">
                    {previewLines.map(([k, v]) => (
                        <div key={k}><span className="text-black/60">{k}:</span> {v}</div>
                    ))}
                </div>
                <p className="mt-2 text-xs text-black/60">
                    Preview for {previewId}. Files download as {previewId}.jpg; extra images for the same Saral Id get _2, _3 and so on.
                </p>
            </Step>

            {/* Action bar */}
            <div className="sticky bottom-3 z-10 rounded-2xl border border-yellow-600/60 bg-white/95 p-4 shadow-lg shadow-black/10 backdrop-blur">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="mr-auto min-w-0">
                        <p className="text-sm font-semibold text-black">
                            <span className="text-yellow-700">{totalSelected}</span> {totalSelected === 1 ? "image" : "images"} selected
                            {selected.size > 0 && <span className="font-normal text-black/60"> from {selected.size} Saral {selected.size === 1 ? "Id" : "IDs"}</span>}
                        </p>
                        {message && (
                            <p className={`truncate text-sm ${message.type === "error" ? "text-yellow-800" : message.type === "done" ? "text-black" : "text-black/60"}`}>
                                {message.text}
                            </p>
                        )}
                    </div>
                    <Btn primary onClick={run} disabled={!totalSelected || busy}>
                        <Icon d={ICONS.download} />
                        {busy ? "Working" : "Label and download ZIP"}
                    </Btn>
                </div>
                {busy && (
                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-black/10" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin={0} aria-valuemax={100}>
                        <div className="h-full bg-yellow-600 transition-all" style={{ width: `${progress}%` }} />
                    </div>
                )}
            </div>
        </div>
    );
}

